import { CodeHighlight } from "@mantine/code-highlight";
import { Alert, Badge, Code, Group, Stack, Text, Title } from "@mantine/core";

import { JournalErrorAlert } from "./JournalErrorAlert";

/**
 * Shared rendering helper for the three rule-fired payload variants
 * (advancement, seeding, withdrawal). The three payload types have
 * structurally identical fields — keeping a single helper here avoids
 * three near-identical components and keeps formatting decisions in one
 * place.
 */
export interface RuleFiredCommonProps {
  ruleName: string;
  ruleId: string;
  ruleSetVersion: number;
  triggerType: string;
  triggerEventTypename: string;
  conditionSource: string;
  conditionResult: {
    matched: boolean;
    value: string | null;
  };
  actionSource: string;
  engineTiming: {
    conditionEvalMs: number;
    actionExecMs: number;
    totalMs: number;
  };
  printOutput: ReadonlyArray<string>;
  printOutputTruncated: boolean;
  error: {
    code: string;
    message: string;
    hint: string | null;
    stackHint: string | null;
  } | null;
  manualOverride: {
    overriddenBy: string;
    reason: string;
  } | null;
  /** Color used for the rule-kind badge (advancement / seeding / withdrawal). */
  kindColor: string;
  /** Short label used for the rule-kind badge. */
  kindLabel: string;
}

export function RuleFiredCommon(props: RuleFiredCommonProps) {
  const {
    ruleName,
    ruleId,
    ruleSetVersion,
    triggerType,
    triggerEventTypename,
    conditionSource,
    conditionResult,
    actionSource,
    engineTiming,
    printOutput,
    printOutputTruncated,
    error,
    manualOverride,
    kindColor,
    kindLabel,
  } = props;

  return (
    <Stack gap="xs">
      <Group gap="xs" wrap="wrap">
        <Badge color={kindColor} size="xs">
          {kindLabel}
        </Badge>
        <Badge color={conditionResult.matched ? "green" : "gray"} size="xs">
          {conditionResult.matched ? "MATCHED" : "NO MATCH"}
        </Badge>
        <Title order={6}>{ruleName}</Title>
        <Text size="xs" c="dimmed">
          {ruleId.slice(0, 8)} · v{ruleSetVersion}
        </Text>
        {manualOverride && (
          <Badge color="yellow" size="xs">
            MANUAL OVERRIDE
          </Badge>
        )}
      </Group>
      <Text size="xs" c="dimmed">
        trigger: {triggerType} · payload: {triggerEventTypename}
      </Text>
      <Stack gap={2}>
        <Text size="xs" fw={500}>
          Condition
        </Text>
        <CodeHighlight code={conditionSource} language="lua" />
        {conditionResult.value !== null && (
          <Text size="xs" c="dimmed">
            returned: {conditionResult.value}
          </Text>
        )}
      </Stack>
      <Stack gap={2}>
        <Text size="xs" fw={500}>
          Action
        </Text>
        <CodeHighlight code={actionSource} language="lua" />
      </Stack>
      <Text size="xs" c="dimmed">
        timing: condition {engineTiming.conditionEvalMs.toFixed(2)}ms · action{" "}
        {engineTiming.actionExecMs.toFixed(2)}ms · total{" "}
        {engineTiming.totalMs.toFixed(2)}ms
      </Text>
      {printOutput.length > 0 && (
        <Stack gap={2}>
          <Text size="xs" fw={500}>
            print output{printOutputTruncated ? " (truncated)" : ""}
          </Text>
          <Code block>{printOutput.join("\n")}</Code>
        </Stack>
      )}
      {error && <JournalErrorAlert error={error} />}
      {manualOverride && (
        <Alert color="yellow" variant="light" p="xs">
          <Stack gap={2}>
            <Text size="xs">
              Overridden by account {manualOverride.overriddenBy.slice(0, 8)}
            </Text>
            <Text size="xs" c="dimmed">
              {manualOverride.reason}
            </Text>
          </Stack>
        </Alert>
      )}
    </Stack>
  );
}
